import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import React, { useEffect, useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { CardsWithIcon } from '../../../component/cards/cards-with-icon';
import { Chart } from 'primereact/chart';
import dateFormat from '../../../utils/date-format';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import globalUserPage from '../../../stores/global-user-page';
import { UserPaymentServiceWithPriceList } from '../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UserSummaryType } from '../../../api/types/user/user-summary.type';
import { PelecardResponse } from './i-fram-asd';
import PaymentIframe from './i-fram-asd';
import { getPaymentLinkOperations } from './payment-services/operations/get-payment-link-operations';
import { Dialog } from 'primereact/dialog';
import { PaymentResponse } from '../../../api/types/payments/gateway/payment-gateway-response';
import { onPaymentSuccessOperation } from './payment-services/operations/on-payment-success-operation';
import { ProgressSpinner } from 'primereact/progressspinner';
import dateUtils from '../../../utils/date-format';
import { FilterMatchMode } from 'primereact/api';
import '../../../component/styles/table-view-filter-width.css';
import {
  PaymentMethodSelectionDialog,
  PaymentMethod,
  AllocationWithAmount,
} from './payment-methods/payment-method-selection-dialog';
import { PaymentServiceHistoryList } from './payment-services/componenets/payment-service-history-list/payment-service-history-list';
import { AddPaymentServiceDialog } from './payment-services/componenets/add-payment-service-dialog';
interface UserPagePaymentsDataModel {
  user: UserSummaryType;
}

export const UserPagePayments: React.FC<UserPagePaymentsDataModel> = ({
  user,
}) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [paymentUrl, setPaymentUrl] = useState<
    (PaymentResponse & { allocationId: number }) | null
  >(null);
  const [loadingNotifySuccess, setLoadingNotifySuccess] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [allocationsForPayment, setAllocationsForPayment] = useState<
    AllocationWithAmount[]
  >([]);
  const [selectedRows, setSelectedRows] = useState<
    EnrichedUserPaymentService[]
  >([]);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [showAddPaymentServiceDialog, setShowAddPaymentServiceDialog] =
    useState(false);

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'paymentService.paymentServiceName': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    'currentPrices.price': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    totalPaymentAmount: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    'allocations.allocationEntity.createdAt': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    'allocations.programEntity.programName': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    'allocations.userPayments[0].paymentDate': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    paymentStatus: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
  });

  const handlePaymentMethodSelected = async (
    method: PaymentMethod,
    allocations: AllocationWithAmount[],
    additionalData?: any,
  ) => {
    switch (method) {
      case PaymentMethod.CreditCard:
        // For credit card, if multiple allocations, we need to handle differently
        // For now, handle only single allocation for credit card
        if (allocations.length === 1) {
          await handleGenerateLink(
            allocations[0].allocationId,
            allocations[0].amount,
          );
        } else {
          console.error(
            'Credit card payment not supported for multiple allocations yet',
          );
        }
        break;

      case PaymentMethod.BankTransfer:
      case PaymentMethod.Check:
      case PaymentMethod.Scholarship:
      case PaymentMethod.Cash:
        // These are handled in the dialog via API call
        // Just close dialog and clear selection
        setShowPaymentMethodDialog(false);
        setAllocationsForPayment([]);
        setSelectedRows([]);
        break;
    }
  };

  const handleOnPayment = async (
    allocationId: number,
    paymentServiceName: string,
    amount: number,
  ) => {
    setAllocationsForPayment([
      {
        allocationId,
        paymentServiceName,
        maxAmount: amount,
        amount: amount,
      },
    ]);
    setShowPaymentMethodDialog(true);
  };

  const handlePaySelectedRows = () => {
    if (selectedRows.length === 0) return;

    const allocations = selectedRows
      .filter((row) => {
        const totalPaid =
          row.userPayments?.reduce(
            (sum, payment) => sum + payment.itemAmount,
            0,
          ) ?? 0;
        const remainingAmount = row.currentPrices.price - totalPaid;
        return remainingAmount > 0;
      })
      .map((row) => {
        const totalPaid =
          row.userPayments?.reduce(
            (sum, payment) => sum + payment.itemAmount,
            0,
          ) ?? 0;
        const remainingAmount = row.currentPrices.price - totalPaid;

        return {
          allocationId: row.allocations.allocationEntity.id,
          paymentServiceName: row.paymentService.paymentServiceName,
          maxAmount: remainingAmount,
          amount: remainingAmount,
        };
      });

    if (allocations.length === 0) {
      console.log('No allocations with outstanding amounts');
      return;
    }

    setAllocationsForPayment(allocations);
    setShowPaymentMethodDialog(true);
  };

  const handleGenerateLink = async (allocationId: number, amount: number) => {
    await getPaymentLinkOperations(
      allocationId,
      user.id,
      amount,
      setPaymentUrl,
    );
  };

  const handleOnSuccessPayment = async (data: PelecardResponse) => {
    await onPaymentSuccessOperation(
      paymentUrl?.allocationId,
      user.id,
      paymentUrl?.pendingPaymentId,
      setLoadingNotifySuccess,
      setPaymentUrl,
    );
  };

  const resetLink = () => setPaymentUrl(null);

  const totalAllocation =
    globalUserPage?.studentDetails?.paymentServiceDetails ?? [];

  const fullyPaidCount =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (count, service) => {
        const totalPayments = service.userPayments?.reduce(
          (sum, payment) => sum + payment.itemAmount,
          0,
        );
        const requiredPrice = service.currentPrices.price;
        return count + (totalPayments >= requiredPrice ? 1 : 0);
      },
      0,
    ) ?? 0;

  const totalOverallPayments =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (total, service) => {
        const servicePayments = service.userPayments
          ? service.userPayments.reduce(
              (sum, payment) => sum + payment.itemAmount,
              0,
            )
          : 0;
        return total + servicePayments;
      },
      0,
    ) ?? 0;

  const totalCurrentPrices =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (total, service) => {
        return total + (service.currentPrices?.price ?? 0);
      },
      0,
    ) ?? 0;

  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const data = {
      labels: ['חובות', 'הכנסות'],
      datasets: [
        {
          data: [
            totalCurrentPrices - totalOverallPayments,
            totalOverallPayments,
          ],
          backgroundColor: [
            documentStyle.getPropertyValue('--blue-500'),
            documentStyle.getPropertyValue('--green-500'),
          ],
          hoverBackgroundColor: [
            documentStyle.getPropertyValue('--blue-400'),
            documentStyle.getPropertyValue('--green-400'),
          ],
        },
      ],
    };
    const options = {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [totalCurrentPrices, totalOverallPayments]);

  type PaymentStatus = 'שולם' | 'שולם חלקית' | 'לא שולם';
  const payedStatus: PaymentStatus = 'שולם';
  const partialStatus: PaymentStatus = 'שולם חלקית';
  const missingPaymentStatus: PaymentStatus = 'לא שולם';
  const [statuses] = useState([
    payedStatus,
    partialStatus,
    missingPaymentStatus,
  ]);
  const statusItemTemplate = (option: PaymentStatus) => {
    return option === 'שולם' ? (
      <Tag style={{ width: '70px' }} severity="success" value="שולם"></Tag>
    ) : option === 'שולם חלקית' ? (
      <Tag
        style={{ width: '70px' }}
        severity="warning"
        value="שולם חלקית"
      ></Tag>
    ) : (
      <Tag style={{ width: '70px' }} severity="danger" value="לא שולם"></Tag>
    );
  };

  const statusFilterElement = (options: ColumnFilterElementTemplateOptions) => {
    console.log('Status filter element options:', options);

    return (
      <Dropdown
        value={options.value}
        options={statuses}
        onChange={(e) => options.filterApplyCallback(e.value)}
        itemTemplate={statusItemTemplate}
        className="p-column-filter"
        style={{
          width: '130px',
          height: '30px',
          alignItems: 'center',
        }}
      />
    );
  };

  type EnrichedUserPaymentService = UserPaymentServiceWithPriceList & {
    totalPaymentAmount: number;
    paymentStatus: PaymentStatus;
    installmentAmount: number;
  };

  const calculatePaymentStatus = (
    service: UserPaymentServiceWithPriceList,
  ): PaymentStatus => {
    const totalPaymentAmount =
      service.userPayments?.reduce(
        (sum, payment) => sum + payment.itemAmount,
        0,
      ) ?? 0;
    const currentPriceToPay = service.currentPrices.price;

    if (totalPaymentAmount === currentPriceToPay) {
      return 'שולם';
    } else if (totalPaymentAmount > 0) {
      return 'שולם חלקית';
    }
    return 'לא שולם';
  };

  // Create a function to calculate total payment (separated for reusability and cleaner code)
  const calculateTotalPayment = (
    service: UserPaymentServiceWithPriceList,
  ): number => {
    return (
      service.userPayments?.reduce(
        (sum, payment) => sum + payment.itemAmount,
        0,
      ) ?? 0
    );
  };

  // Get all installment plans for passing to PaymentServiceHistoryList
  const creditCardInstallmentPlans =
    globalUserPage.studentDetails?.installments ?? [];
  const bankTransferInstallmentPlans =
    globalUserPage.studentDetails?.installmentsBankTransfer ?? [];

  // Create a function to calculate installment amount for a specific allocation
  const calculateInstallmentAmountForAllocation = (
    allocationId: number,
  ): number => {
    let totalInstallmentAmount = 0;

    // Check credit card installment plans
    creditCardInstallmentPlans.forEach((plan) => {
      const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
        (item) => item.userAllocationId === allocationId,
      );
      if (allocationItem) {
        totalInstallmentAmount += allocationItem.allocationAmount;
      }
    });

    // Check bank transfer installment plans
    bankTransferInstallmentPlans.forEach((plan) => {
      const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
        (item) => item.userAllocationId === allocationId,
      );
      if (allocationItem) {
        totalInstallmentAmount += allocationItem.allocationAmount;
      }
    });

    return totalInstallmentAmount;
  };

  // Create the enriched data array
  const enrichedPaymentServices: EnrichedUserPaymentService[] =
    globalUserPage.studentDetails?.paymentServiceDetails?.map((service) => ({
      ...service,
      totalPaymentAmount: calculateTotalPayment(service),
      paymentStatus: calculatePaymentStatus(service),
      installmentAmount: calculateInstallmentAmountForAllocation(
        service.allocations.allocationEntity.id,
      ),
    })) ?? [];
  const handleRowClick = React.useCallback((data: any) => {
    if (data.userPayments?.length > 0) {
      const id = data.allocations.allocationEntity.id;
      setExpandedRows((prev) => {
        const newState = { ...prev };
        if (newState[id]) {
          delete newState[id];
        } else {
          newState[id] = true;
        }
        return newState;
      });
    }
  }, []);

  return (
    <div style={{ width: '100%' }}>
      <PaymentMethodSelectionDialog
        visible={showPaymentMethodDialog}
        onHide={() => {
          setShowPaymentMethodDialog(false);
          setAllocationsForPayment([]);
        }}
        allocations={allocationsForPayment}
        user={user}
        onPaymentMethodSelected={handlePaymentMethodSelected}
      />
      {!!paymentUrl && (
        <Dialog
          header="תשלום"
          visible={paymentUrl !== null}
          onHide={resetLink}
          style={{
            width: '700px',
            height: '90vh',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          {loadingNotifySuccess ? (
            <ProgressSpinner
              style={{
                width: '400px',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
              }}
              strokeWidth="3"
              fill="var(--surface-ground)"
              animationDuration=".5s"
            />
          ) : (
            <PaymentIframe
              src={paymentUrl!.url}
              successLink={paymentUrl!.successUrl}
              failLink={paymentUrl!.failUrl}
              onPaymentSuccess={(data: PelecardResponse) => {
                handleOnSuccessPayment(data);
              }}
              onPaymentError={(data?: PelecardResponse) => {
                //
              }}
            />
          )}
        </Dialog>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '600px',
          /*flexWrap: 'wrap', // Allows items to wrap to the next row*/
          gap: '16px', // Adds space between items
          height: 'auto',
        }}
      >
        <CardsWithIcon
          title={'כמות שרותים'}
          icon="pi pi-shopping-cart"
          iconBgColor={'#D7EBFF'}
          bodyText={[
            `${fullyPaidCount} מתוך ${totalAllocation.length} נרכש`,
            `${totalOverallPayments}₪ נכנס מתוך ${totalCurrentPrices}₪`,
          ]}
        />
        <Chart
          style={{ height: '200px' }}
          type="pie"
          data={chartData}
          options={chartOptions}
        />
      </div>

      <div
        style={{
          marginTop: '16px',
          marginBottom: '8px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
        }}
      >
        <Button
          label="הוסף שרות תשלום"
          icon="pi pi-plus"
          onClick={() => setShowAddPaymentServiceDialog(true)}
          style={{
            backgroundColor: '#2196F3',
            border: 'none',
            color: 'white',
          }}
        />
        {selectedRows.length > 0 && (
          <Button
            label={`שלם עבור ${selectedRows.length} שרותים נבחרים`}
            icon="pi pi-credit-card"
            onClick={handlePaySelectedRows}
            style={{
              backgroundColor: '#00C853',
              border: 'none',
              color: 'white',
            }}
          />
        )}
      </div>

      <AddPaymentServiceDialog
        visible={showAddPaymentServiceDialog}
        onHide={() => setShowAddPaymentServiceDialog(false)}
        user={user}
      />

      <DataTable
        value={enrichedPaymentServices}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={(data) => (
          <PaymentServiceHistoryList
            payments={data.userPayments}
            totalPrice={data.currentPrices.price}
            user={user}
            allocationId={data.allocations.allocationEntity.id}
            creditCardInstallmentPlans={creditCardInstallmentPlans}
            bankTransferInstallmentPlans={bankTransferInstallmentPlans}
          />
        )}
        onRowClick={(e) => handleRowClick(e.data)}
        dataKey="allocations.allocationEntity.id"
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode="checkbox"
        selection={selectedRows}
        onSelectionChange={(e) => {
          setSelectedRows(e.value as EnrichedUserPaymentService[]);
        }}
        filterDisplay={'row'}
        groupRowsBy="dsf"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        scrollable
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: '3rem' }}
          style={{ width: '3rem', textAlign: 'center' }}
          frozen={true}
        />
        <Column
          expander={true}
          style={{ width: '3rem', textAlign: 'center' }}
          frozen={true}
          body={(data) => {
            const hasPayments = data.userPayments?.length > 0;
            const isExpanded =
              expandedRows[data.allocations.allocationEntity.id];

            return (
              <div
                className={`expander-icon ${hasPayments ? 'has-payments' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                }}
              >
                <i
                  className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                  style={{
                    color: hasPayments ? '#2196F3' : '#ccc',
                    fontSize: '1rem',
                    transform: `translateY(${isExpanded ? '-1px' : '1px'})`,
                    transition: 'transform 0.15s ease-in-out',
                  }}
                />
              </div>
            );
          }}
        />
        <Column
          style={{
            textAlign: 'right',
            height: '40px',
            minWidth: '150px',
          }}
          field="paymentService.paymentServiceName"
          header="שם שרות"
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          filter={true}
          filterField="paymentService.paymentServiceName"
          filterPlaceholder="שם שרות"
          filterMatchMode="contains"
          showFilterMenu={false}
        />

        <Column
          style={{ textAlign: 'right', height: '40px', maxWidth: '80px' }}
          field="currentPrices.price"
          header="סכום"
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
            minWidth: '150px',
          }}
          body={(rowData: UserPaymentServiceWithPriceList) => (
            <div>₪{rowData.currentPrices.price}</div>
          )}
          filter={true}
          filterField="currentPrices.price"
          filterPlaceholder="שם שרות"
          filterMatchMode="contains"
          showFilterMenu={false}
        />

        <Column
          style={{ textAlign: 'right', height: '40px', maxWidth: '80px' }}
          header="שולם"
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
            minWidth: '150px',
          }}
          field={'totalPaymentAmount'}
          filter={true}
          filterField="totalPaymentAmount"
          filterPlaceholder="סכום"
          showFilterMenu={false}
        />

        <Column
          style={{ textAlign: 'right', height: '40px', maxWidth: '120px' }}
          header={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                alignItems: 'center',
              }}
            >
              הסדר תשלום
              <Tooltip target=".installment-info-icon" position="top" />
              <i
                className="pi pi-info-circle installment-info-icon"
                data-pr-tooltip="כלל התשלומים המתוכננים לשלם בהוראות הקבע"
                style={{
                  cursor: 'pointer',
                  color: '#6366F1',
                  fontSize: '1rem',
                }}
              />
            </div>
          }
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
            minWidth: '120px',
          }}
          field={'installmentAmount'}
          body={(rowData: EnrichedUserPaymentService) => (
            <div
              style={{
                color: rowData.installmentAmount > 0 ? '#1976D2' : '#9CA3AF',
                fontWeight: rowData.installmentAmount > 0 ? 'bold' : 'normal',
              }}
            >
              {rowData.installmentAmount > 0
                ? `₪${rowData.installmentAmount.toLocaleString()}`
                : '-'}
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px', maxWidth: '100px' }}
          header="תאריך יצירה"
          field={'allocations.allocationEntity.createdAt'}
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: UserPaymentServiceWithPriceList, options) => (
            <div>
              {dateFormat.displayFormatDate(
                rowData.allocations.allocationEntity.createdAt,
              )}
            </div>
          )}
          filterElement={(options) => (
            <input
              type="text"
              value={options.value || ''}
              onChange={(e) => options.filterApplyCallback(e.target.value)}
              style={{ width: '80px', paddingTop: '5px', paddingBottom: '5px' }}
              className="p-inputtext p-component"
            />
          )}
          filterFunction={(value, filter: string) => {
            if (!filter || filter.trim() === '') {
              return true;
            }

            const dateStr = dateFormat.displayFormatDate(value);
            return dateStr.toLowerCase().includes(filter.toLowerCase());
          }}
          filter={true}
          filterField="allocations.allocationEntity.createdAt"
          filterMatchMode="contains"
          showFilterMenu={false}
          showClearButton={false}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header="תוכנית לימודים"
          field={'allocations.programEntity.programName'}
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          filterElement={(options) => (
            <input
              type="text"
              value={options.value || ''}
              onChange={(e) => {
                options.filterApplyCallback(e.target.value);
              }}
              style={{
                width: '80px',
                height: '30px',
                padding: '5px',
              }}
              className="p-inputtext p-component"
              placeholder="מוען"
            />
          )}
          filter={true}
          filterField="allocations.programEntity.programName" // Updated to match the actual data path
          filterMatchMode="contains"
          showFilterMenu={false}
        />

        <Column
          style={{ textAlign: 'center', height: '40px', maxWidth: '100px' }}
          header="תאריך תשלום"
          field="allocations.userPayments[0]" // Match the field to your data key
          sortable={true}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
          }}
          filter={true}
          filterField="allocations.userPayments[0].paymentDate"
          filterMatchMode="contains"
          showFilterMenu={false}
          filterElement={(options) => (
            <input
              type="text"
              value={options.value || ''}
              onChange={(e) => {
                const newValue = e.target.value || ''; // Fallback for empty input
                options.filterApplyCallback(newValue); // Apply the filter
              }}
              style={{
                width: '80px',
                paddingTop: '5px',
                paddingBottom: '5px',
              }}
              className="p-inputtext p-component"
            />
          )}
          filterFunction={(value, filter: string) => {
            if (!filter || filter.trim() === '') {
              return true;
            }

            const dateStr = dateFormat.displayFormatDate(value);
            return dateStr.toLowerCase().includes(filter.toLowerCase());
          }}
          body={(rowData: UserPaymentServiceWithPriceList, options) => {
            let date: Date | null = null;
            if (rowData.allocations.userPayments?.length) {
              date = new Date(rowData.allocations.userPayments[0].paymentDate);
            }
            return (
              <div style={{ textAlign: 'right' }}>
                {date ? dateUtils.displayFormatDate(date) : '-'}
              </div>
            );
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px', maxWidth: '300px' }}
          field="paymentStatus"
          header="סטטוס תשלום"
          headerStyle={{
            height: '45px',

            maxHeight: '45px',
            padding: '0',
          }}
          /*filterMenuStyle={{ width: '14rem' }}*/
          showFilterMenu={false}
          filter={true}
          filterElement={statusFilterElement}
          filterMatchMode={FilterMatchMode.EQUALS} // Changed to EQUALS
          body={(rowData: EnrichedUserPaymentService) => {
            const severityMap = {
              שולם: 'success',
              'שולם חלקית': 'warning',
              'לא שולם': 'danger',
            } as const;

            return (
              <Tag
                style={{ width: '70px', textAlign: 'center' }}
                severity={severityMap[rowData.paymentStatus]}
                value={rowData.paymentStatus}
              />
            );
          }}
        />

        <Column
          style={{ textAlign: 'center', height: '40px', width: '3rem' }}
          field="סטטוס"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: UserPaymentServiceWithPriceList, option) => {
            const currentPriceToPay = rowData.currentPrices.price;
            const totalPaymentAmount =
              rowData.userPayments?.reduce(
                (sum, payment) => sum + payment.itemAmount,
                0,
              ) ?? 0;
            const remainingAmount = currentPriceToPay - totalPaymentAmount;

            return (
              <Button
                style={{
                  height: '25px',
                  fontSize: '14px',
                  textAlign: 'center',
                  visibility: remainingAmount <= 0 ? 'hidden' : 'visible',
                  padding: '0px',
                  paddingRight: '8px',
                  paddingLeft: '8px',
                }}
                label="לתשלום"
                outlined={true}
                raised={true}
                onClick={() => {
                  handleOnPayment(
                    rowData.allocations.allocationEntity.id,
                    rowData.paymentService.paymentServiceName,
                    remainingAmount,
                  );
                }}
              />
            );
          }}
        />
      </DataTable>

      <style>{`
        .payment-summary {
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          border: 1px solid #e0e0e0;
        }
        
        .p-datatable .p-datatable-tbody > tr {
          cursor: pointer;
          transition: background-color 0.15s ease-in-out;
        }
        
        .p-datatable .p-datatable-tbody > tr:not(.p-highlight):hover {
          background-color: #f0f7ff !important;
        }
        
        .p-datatable .p-datatable-tbody > tr.p-row-expanded {
          background-color: #f8f9fa;
        }
        
        .p-datatable .p-datatable-tbody > tr.p-row-expanded > td {
          border-bottom: none;
        }
        
        .expanded-payments-table .p-datatable-wrapper {
          border-radius: 8px;
          overflow: hidden;
        }
        
        .p-row-expanded > td {
          background-color: #f8f9fa !important;
        }
        
        .expander-icon {
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};
